import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  MessagePayload,
  onMessage,
} from 'firebase/messaging';
import { FirebaseWebConfig, GlobalService } from 'src/app/global.config';

export type PushPlatform = 'android' | 'ios' | 'web';

const SW_CONFIG_DB = 'accent-firebase-sw';
const SW_CONFIG_STORE = 'config';
const SW_CONFIG_KEY = 'firebase';

@Injectable({
  providedIn: 'root',
})
export class WebPushService {
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private foregroundBound = false;

  constructor(private globalService: GlobalService) {}

  getFirebaseConfig(): FirebaseWebConfig | null {
    return this.globalService.getFirebaseConfig();
  }

  isConfigured(): boolean {
    const cfg = this.getFirebaseConfig();
    return !!(cfg?.apiKey && cfg?.projectId && cfg?.messagingSenderId && cfg?.appId && cfg?.vapidKey);
  }

  async isAvailable(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) {
      return false;
    }
    if (!this.isConfigured()) {
      return false;
    }
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return false;
    }
    try {
      return await isSupported();
    } catch {
      return false;
    }
  }

  async requestPermissionAndToken(
    onForegroundMessage?: (payload: MessagePayload) => void
  ): Promise<string | null> {
    const cfg = this.getFirebaseConfig();
    if (!(await this.isAvailable()) || !cfg) {
      if (!Capacitor.isNativePlatform() && !this.isConfigured()) {
        console.warn(
          '[WebPush] Skipped: active profile has no firebase web config (apiKey/appId/vapidKey).'
        );
      }
      return null;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[WebPush] Notification permission not granted:', permission);
      return null;
    }

    await this.persistConfigForServiceWorker(cfg);
    await this.ensureMessaging(cfg, onForegroundMessage);

    if (!this.messaging) {
      return null;
    }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;

    // Ensure SW has the profile firebase config (survives SW restarts via IndexedDB).
    registration.active?.postMessage({ type: 'FIREBASE_CONFIG', config: this.toSwConfig(cfg) });

    const token = await getToken(this.messaging, {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: registration,
    });

    return token || null;
  }

  resolveBrowserDeviceId(): string {
    const key = 'web_push_device_id';
    let id = localStorage.getItem(key);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? `web-${crypto.randomUUID()}`
          : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(key, id);
    }
    return id;
  }

  private toSwConfig(cfg: FirebaseWebConfig): Omit<FirebaseWebConfig, 'vapidKey'> {
    return {
      apiKey: cfg.apiKey,
      authDomain: cfg.authDomain,
      projectId: cfg.projectId,
      storageBucket: cfg.storageBucket,
      messagingSenderId: cfg.messagingSenderId,
      appId: cfg.appId,
      measurementId: cfg.measurementId,
    };
  }

  private persistConfigForServiceWorker(cfg: FirebaseWebConfig): Promise<void> {
    const value = this.toSwConfig(cfg);
    return new Promise((resolve, reject) => {
      const open = indexedDB.open(SW_CONFIG_DB, 1);
      open.onupgradeneeded = () => {
        const db = open.result;
        if (!db.objectStoreNames.contains(SW_CONFIG_STORE)) {
          db.createObjectStore(SW_CONFIG_STORE);
        }
      };
      open.onsuccess = () => {
        const db = open.result;
        const tx = db.transaction(SW_CONFIG_STORE, 'readwrite');
        tx.objectStore(SW_CONFIG_STORE).put(value, SW_CONFIG_KEY);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      };
      open.onerror = () => reject(open.error);
    });
  }

  private async ensureMessaging(
    cfg: FirebaseWebConfig,
    onForegroundMessage?: (payload: MessagePayload) => void
  ): Promise<void> {
    if (!this.messaging) {
      this.app = getApps().length
        ? getApps()[0]
        : initializeApp({
            apiKey: cfg.apiKey,
            authDomain: cfg.authDomain,
            projectId: cfg.projectId,
            storageBucket: cfg.storageBucket,
            messagingSenderId: cfg.messagingSenderId,
            appId: cfg.appId,
            measurementId: cfg.measurementId,
          });
      this.messaging = getMessaging(this.app);
    }

    if (!this.foregroundBound) {
      this.foregroundBound = true;
      onMessage(this.messaging, (payload) => {
        console.log('FCM foreground', payload);
        this.showBrowserNotification(payload);
        if (onForegroundMessage) {
          onForegroundMessage(payload);
        }
      });
    }
  }

  /** Show a system notification while the tab is focused (foreground). */
  private showBrowserNotification(payload: MessagePayload): void {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }
    const title =
      payload.notification?.title ||
      (payload.data && payload.data['title']) ||
      'Notification';
    const body =
      payload.notification?.body ||
      (payload.data && payload.data['body']) ||
      '';

    try {
      const n = new Notification(title, {
        body,
        tag: 'accent-alert-fcm',
        requireInteraction: false,
        data: payload.data || {},
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch (e) {
      console.error('[WebPush] Failed to display foreground notification', e);
    }
  }
}
